import { Icon } from "@deligoo/ui"
import clsx from "clsx"
import { CSSProperties, memo } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { DriverHeader } from "@/components/DriverHeader"
import { OrderPoint, ReturnedOrderPoint } from "@/components/OrderPoint"
import { useOrdersContextMenuTrigger } from "@/modules/OrderContextMenu"
import { selectOrderById, selectReturnedOrderIdData } from "@/store/orders"
import { setShowOrderDetails } from "@/store/ui"
import { Order } from "@/types"
import { OrderUtils } from "@/utils"

import cls from "./Orders.module.scss"

type OrderItemProps = {
  orderId: Order["id"]
  header?: boolean
  style?: CSSProperties
}

export const OrderItem = memo(({ orderId, header = true, style }: OrderItemProps) => {
  const contextMenuRef = useOrdersContextMenuTrigger({ orderId })

  const isFocused = useAppSelector((state) => state.ui.focusedOrderId === orderId)
  const returnedOrder = useAppSelector(selectReturnedOrderIdData(orderId))

  const dispatch = useDispatch()

  return (
    <Card.Section className={clsx(cls.orderCard, isFocused && cls.focused)} style={style} ref={contextMenuRef}>
      {header && (
        <div className={cls.header}>
          <OrderHeader orderId={orderId} />
        </div>
      )}

      <div className={cls.orderPoints}>
        {returnedOrder ? (
          <ReturnedOrderPoint {...returnedOrder} />
        ) : (
          <OrderPoint
            orderId={orderId}
            type="pickup"
            showDetails={() => dispatch(setShowOrderDetails({ id: orderId, type: "pickup" }))}
          />
        )}

        <OrderPoint
          orderId={orderId}
          type="delivery"
          showDetails={() => dispatch(setShowOrderDetails({ id: orderId, type: "delivery" }))}
        />

        <ReassignedIcon orderId={orderId} />
      </div>
    </Card.Section>
  )
})

const ReassignedIcon = memo(({ orderId }: { orderId: Order["id"] }) => {
  const order = useAppSelector(selectOrderById(orderId), shallowEqual)
  const isReassigned = order?.reassigned || order?.force_assignment
  const isAutomaticReassigned = order?.automatic_reassignment

  if (!order || !isReassigned) return null

  if (isAutomaticReassigned) {
    return (
      <div className={cls.reassigned}>
        <Icon name="exchange" color="dark" size="large" className={cls.icon} />
        <Icon name="computer" color="dark" size="small" className={cls.icon} />
      </div>
    )
  } 
    return (
      <div className={cls.reassigned}>
        <Icon name="exchange" color="dark" size="large" className={cls.icon} />
        {order.force_assignment && <Icon name="flash" color="dark" size="small" className={cls.icon} />}
      </div>
    )
  
})

const OrderHeader = ({ orderId }: { orderId: Order["id"] }) => {
  const order = useAppSelector(selectOrderById(orderId), shallowEqual)

  const { t } = useTranslation()

  if (!order) return null

  return (
    <DriverHeader
      driverId={order.driver_id}
      orderId={order.id}
      subheader={
        <span className={`text-desc status-${OrderUtils.getCurrentState(order)}`}>
          {`${t(`orders:${OrderUtils.getCurrentState(order)}`)} ${OrderUtils.getPointCounter(order)}`}
        </span>
      }
      action={<ContextMenu.Button onClick={openContextMenu} />}
    />
  )
}
